import React from 'react';
import { Icon } from './icons.jsx';
// sistema-modals.jsx — Fase 2: modais reais para a seção Sistema
// ExportHistoryModal, SecuritySettingsModal, RetentionConfigModal,
// PlansHistoryModal, SyncPermissionsModal, ComparePlansModal, NewPlanModal, ExportMdModal

const { useState: useSM, useEffect: useEffectSM } = React;

// ── Helpers de UI ─────────────────────────────────────────────────────────────
const SMLbl = ({ children }) => (
  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>{children}</div>
);
const SMInp = ({ style, ...p }) => (
  <input style={{ width: '100%', height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', ...style }} {...p} />
);
const SMSel = ({ style, ...p }) => (
  <select className="select" style={{ width: '100%', ...style }} {...p} />
);
const SMChk = ({ checked, onChange, label, sub }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
    <input type="checkbox" checked={checked} onChange={onChange} style={{ width: 14, height: 14, marginTop: 3, accentColor: 'var(--primary)' }} />
    <div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
    </div>
  </label>
);
const SMSecTitle = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>{children}</div>
);
const ModalOverlay = ({ onClose, children, wide }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', backdropFilter: 'blur(4px)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
    <div onClick={e => e.stopPropagation()} style={{ width: wide ? 720 : 520, maxHeight: '90vh', background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {children}
    </div>
  </div>
);
const MHead = ({ title, sub, onClose }) => (
  <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{sub}</div>}
    </div>
    {onClose && <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>}
  </div>
);
const MBody = ({ children, style }) => (
  <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto', ...style }}>
    {children}
  </div>
);
const MFoot = ({ children }) => (
  <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'var(--bg-subtle)', flexShrink: 0 }}>
    {children}
  </div>
);
const Spinner = () => (
  <div style={{ width: 52, height: 52, borderRadius: '50%', border: '3px solid var(--brand-navy)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
);
const ProgressBar = ({ value }) => (
  <div style={{ width: '100%', height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
    <div style={{ height: '100%', width: value + '%', background: 'var(--brand-navy)', borderRadius: 3, transition: 'width 140ms' }} />
  </div>
);

// ── 1. ExportHistoryModal ─────────────────────────────────────────────────────
const ExportHistoryModal = ({ open, onClose, onExport }) => {
  const [period,         setPeriod]         = useSM('30d');
  const [customFrom,     setCustomFrom]     = useSM('');
  const [customTo,       setCustomTo]       = useSM('');
  const [eventType,      setEventType]      = useSM('all');
  const [format,         setFormat]         = useSM('csv');
  const [includeIP,      setIncludeIP]      = useSM(true);
  const [includeBrowser, setIncludeBrowser] = useSM(false);
  const [includeDetails, setIncludeDetails] = useSM(true);
  const [exporting,      setExporting]      = useSM(false);

  if (!open) return null;

  const handleExport = async () => {
    setExporting(true);
    await new Promise(r => setTimeout(r, 900));
    setExporting(false);
    onExport({ period, eventType, format, includeIP, includeBrowser, includeDetails });
  };

  return (
    <ModalOverlay onClose={onClose}>
      <MHead title="Exportar histórico de acessos" sub="Gera arquivo com registros de auditoria da equipe interna" onClose={onClose} />
      <MBody>
        <div>
          <SMLbl>Período</SMLbl>
          <SMSel value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="current">Mês atual</option>
            <option value="prev">Mês anterior</option>
            <option value="custom">Personalizado</option>
          </SMSel>
        </div>
        {period === 'custom' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><SMLbl>Data inicial</SMLbl><SMInp type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} /></div>
            <div><SMLbl>Data final</SMLbl><SMInp type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} /></div>
          </div>
        )}
        <div>
          <SMLbl>Tipo de evento</SMLbl>
          <SMSel value={eventType} onChange={e => setEventType(e.target.value)}>
            <option value="all">Todos</option>
            <option value="login">Login</option>
            <option value="profile">Alteração de perfil</option>
            <option value="invite">Convite de usuário</option>
            <option value="remove">Remoção de usuário</option>
            <option value="perm">Alteração de permissões</option>
          </SMSel>
        </div>
        <div>
          <SMLbl>Formato</SMLbl>
          <div style={{ display: 'flex', gap: 8 }}>
            {['csv', 'xlsx', 'pdf'].map(f => (
              <button key={f} onClick={() => setFormat(f)} style={{ flex: 1, padding: '9px 0', border: `2px solid ${format === f ? 'var(--brand-navy)' : 'var(--border)'}`, borderRadius: 8, background: format === f ? 'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))' : 'var(--bg-elevated)', color: format === f ? 'var(--brand-navy)' : 'var(--text-muted)', fontWeight: format === f ? 700 : 400, fontSize: 13, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                .{f}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SMLbl>Opções</SMLbl>
          <SMChk checked={includeIP} onChange={e => setIncludeIP(e.target.checked)} label="Incluir endereço IP" sub="Exibe o IP de origem de cada evento" />
          <SMChk checked={includeBrowser} onChange={e => setIncludeBrowser(e.target.checked)} label="Incluir agente / navegador" sub="User-Agent completo do cliente" />
          <SMChk checked={includeDetails} onChange={e => setIncludeDetails(e.target.checked)} label="Incluir alterações detalhadas" sub="Estado antes e depois de cada ação" />
        </div>
        <div style={{ padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          Esta exportação será registrada no log de auditoria.
        </div>
      </MBody>
      <MFoot>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={handleExport} disabled={exporting}>
          {exporting
            ? <><Icon name="refresh" size={13} style={{ animation: 'spin 1s linear infinite' }} />Gerando…</>
            : <><Icon name="download" size={13} />Exportar</>}
        </button>
      </MFoot>
    </ModalOverlay>
  );
};

// ── 2. SecuritySettingsModal ──────────────────────────────────────────────────
const SecuritySettingsModal = ({ open, onClose, onSave }) => {
  const [cfg, setCfg] = useSM({
    minChars: 8, requireNumber: true, requireSpecial: true, passwordExpireDays: 90,
    mfaAdmins: true, mfaSupport: false,
    sessionExpireHours: 8,
    allowedIPs: '', blockOutsideBR: false,
    alertSuspiciousLogin: true, alertCriticalPermission: true,
  });
  const [saving, setSaving] = useSM(false);
  const set = k => e => setCfg(c => ({ ...c, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 700));
    setSaving(false);
    onSave(cfg);
  };
  const handleRevoke = () => onSave({ ...cfg, _revokeAll: true });

  return (
    <ModalOverlay onClose={onClose} wide>
      <MHead title="Segurança da equipe interna" sub="Políticas aplicadas a todos os usuários internos do Super Admin" onClose={onClose} />
      <MBody>
        <div>
          <SMSecTitle>Política de senha</SMSecTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <SMLbl>Mínimo de caracteres</SMLbl>
              <SMInp type="number" min={6} max={32} value={cfg.minChars} onChange={set('minChars')} />
            </div>
            <div>
              <SMLbl>Expiração de senha</SMLbl>
              <SMSel value={cfg.passwordExpireDays} onChange={e => setCfg(c => ({ ...c, passwordExpireDays: +e.target.value }))}>
                <option value={0}>Nunca expirar</option>
                <option value={30}>30 dias</option>
                <option value={60}>60 dias</option>
                <option value={90}>90 dias</option>
                <option value={180}>180 dias</option>
              </SMSel>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <SMChk checked={cfg.requireNumber} onChange={set('requireNumber')} label="Exigir número" />
            <SMChk checked={cfg.requireSpecial} onChange={set('requireSpecial')} label="Exigir caractere especial" />
          </div>
        </div>

        <div>
          <SMSecTitle>2FA / MFA</SMSecTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SMChk checked={cfg.mfaAdmins} onChange={set('mfaAdmins')} label="Exigir 2FA para administradores" sub="Perfis: Super Admin, Admin, Owner" />
            <SMChk checked={cfg.mfaSupport} onChange={set('mfaSupport')} label="Exigir 2FA para suporte" sub="Perfis: Suporte N1, N2, Gerente de Suporte" />
          </div>
        </div>

        <div>
          <SMSecTitle>Sessões</SMSecTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
            <div>
              <SMLbl>Expiração por inatividade</SMLbl>
              <SMSel value={cfg.sessionExpireHours} onChange={e => setCfg(c => ({ ...c, sessionExpireHours: +e.target.value }))}>
                <option value={1}>1 hora</option>
                <option value={4}>4 horas</option>
                <option value={8}>8 horas</option>
                <option value={24}>24 horas</option>
                <option value={0}>Não expirar</option>
              </SMSel>
            </div>
            <button className="btn danger-outline" style={{ whiteSpace: 'nowrap' }} onClick={handleRevoke}>
              <Icon name="lock" size={13} />Revogar todas as sessões
            </button>
          </div>
        </div>

        <div>
          <SMSecTitle>Restrições de acesso</SMSecTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <SMLbl>Permitir acesso apenas destes IPs (CIDR ou lista separada por vírgula)</SMLbl>
              <SMInp placeholder="ex: 177.142.0.0/24, 200.100.50.10  —  vazio = sem restrição" value={cfg.allowedIPs} onChange={set('allowedIPs')} />
            </div>
            <SMChk checked={cfg.blockOutsideBR} onChange={set('blockOutsideBR')} label="Bloquear login fora do Brasil" sub="Rejeita autenticações de IPs com geolocalização não-BR" />
          </div>
        </div>

        <div>
          <SMSecTitle>Alertas</SMSecTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SMChk checked={cfg.alertSuspiciousLogin} onChange={set('alertSuspiciousLogin')} label="Alertar login suspeito" sub="IP incomum, novo país, múltiplas tentativas falhas" />
            <SMChk checked={cfg.alertCriticalPermission} onChange={set('alertCriticalPermission')} label="Alertar mudança de permissão crítica" sub="Alteração de roles admin ou owner" />
          </div>
        </div>
      </MBody>
      <MFoot>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={handleSave} disabled={saving}>
          {saving
            ? <><Icon name="refresh" size={13} style={{ animation: 'spin 1s linear infinite' }} />Salvando…</>
            : <><Icon name="check" size={13} />Salvar configurações</>}
        </button>
      </MFoot>
    </ModalOverlay>
  );
};

// ── 3. RetentionConfigModal ───────────────────────────────────────────────────
const RetentionConfigModal = ({ open, onClose, currentPolicy, onSave }) => {
  const [retainFor,     setRetainFor]     = useSM(currentPolicy?.retainFor || '365');
  const [applyTo,       setApplyTo]       = useSM(currentPolicy?.applyTo   || 'all');
  const [exportBefore,  setExportBefore]  = useSM(true);
  const [notifyAdmins,  setNotifyAdmins]  = useSM(true);
  const [blockCritical, setBlockCritical] = useSM(true);
  const [saving,        setSaving]        = useSM(false);

  if (!open) return null;

  const labels = { '30': '30 dias', '90': '90 dias', '180': '180 dias', '365': '1 ano', '730': '2 anos', forever: 'Indefinido' };

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 700));
    setSaving(false);
    onSave({ retainFor, applyTo, exportBefore, notifyAdmins, blockCritical, label: labels[retainFor] });
  };

  return (
    <ModalOverlay onClose={onClose}>
      <MHead title="Configurar retenção de logs" sub="Define por quanto tempo os registros de auditoria serão mantidos" onClose={onClose} />
      <MBody>
        <div>
          <SMLbl>Reter logs por</SMLbl>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {Object.entries(labels).map(([v, l]) => (
              <button key={v} onClick={() => setRetainFor(v)} style={{ padding: '10px 8px', border: `2px solid ${retainFor === v ? 'var(--brand-navy)' : 'var(--border)'}`, borderRadius: 8, background: retainFor === v ? 'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))' : 'var(--bg-elevated)', color: retainFor === v ? 'var(--brand-navy)' : 'var(--text-muted)', fontWeight: retainFor === v ? 700 : 400, fontSize: 13, cursor: 'pointer' }}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <SMLbl>Aplicar a</SMLbl>
          <SMSel value={applyTo} onChange={e => setApplyTo(e.target.value)}>
            <option value="all">Todos os eventos</option>
            <option value="admin">Eventos administrativos</option>
            <option value="support">Eventos de suporte</option>
            <option value="security">Eventos de segurança</option>
            <option value="billing">Eventos de billing</option>
          </SMSel>
        </div>
        <div>
          <SMSecTitle>Comportamento ao excluir</SMSecTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SMChk checked={exportBefore} onChange={e => setExportBefore(e.target.checked)} label="Exportar antes de excluir" sub="Gera backup automático antes de apagar registros antigos" />
            <SMChk checked={notifyAdmins} onChange={e => setNotifyAdmins(e.target.checked)} label="Notificar admins antes da limpeza" sub="Envia alerta 7 dias antes da exclusão automática" />
            <SMChk checked={blockCritical} onChange={e => setBlockCritical(e.target.checked)} label="Bloquear exclusão de eventos críticos" sub="Mantém logs críticos e de segurança independente da política" />
          </div>
        </div>
        {retainFor !== 'forever' && (
          <div style={{ padding: '10px 12px', background: 'var(--warning-bg)', borderRadius: 8, fontSize: 12, color: 'var(--warning)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Icon name="alert" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            Logs com mais de {labels[retainFor]} serão excluídos automaticamente. Esta ação não pode ser revertida.
          </div>
        )}
      </MBody>
      <MFoot>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={handleSave} disabled={saving}>
          {saving
            ? <><Icon name="refresh" size={13} style={{ animation: 'spin 1s linear infinite' }} />Salvando…</>
            : <><Icon name="check" size={13} />Salvar política</>}
        </button>
      </MFoot>
    </ModalOverlay>
  );
};

// ── 4. PlansHistoryModal ──────────────────────────────────────────────────────
const PLANS_HIST_MOCK = [
  { id: 'h1', date: 'Hoje, 09:30',         user: 'Ana Vargas',     plan: 'Pro',        type: 'sync',      resource: 'Permissões',                   before: 'v2.1',       after: 'v2.2',      reason: 'Rotina mensal de sincronização' },
  { id: 'h2', date: '18 Mai, 14:20',       user: 'Pedro Chaves',   plan: 'Pro',        type: 'route_add', resource: '/dashboard/price-tables/',      before: 'Bloqueado',  after: 'Liberado',  reason: 'Feature lançada em produção' },
  { id: 'h3', date: '03 Abr, 11:05',       user: 'Julio Lazzaron', plan: 'Pro',        type: 'price',     resource: 'Preço mensal',                  before: 'R$ 299',     after: 'R$ 349',    reason: 'Reajuste anual aprovado pela diretoria' },
  { id: 'h4', date: '15 Mar, 09:00',       user: 'Ana Vargas',     plan: 'Basic',      type: 'limit',     resource: 'Limite de produtos',            before: '300',        after: '500',       reason: 'Solicitação do time de vendas' },
  { id: 'h5', date: '02 Fev, 16:30',       user: 'Julio Lazzaron', plan: 'Enterprise', type: 'create',    resource: 'Plano Enterprise',              before: '—',          after: 'Rascunho',  reason: 'Novo plano corporativo em desenvolvimento' },
  { id: 'h6', date: '10 Jan, 11:00',       user: 'Pedro Chaves',   plan: 'Basic',      type: 'route_rm',  resource: '/dashboard/reports/export/',    before: 'Liberado',   after: 'Bloqueado', reason: 'Movido exclusivamente para Enterprise' },
  { id: 'h7', date: '05 Jan 2026, 10:00',  user: 'Ana Vargas',     plan: 'Pro',        type: 'module',    resource: 'Módulo Blog',                   before: 'Desativado', after: 'Ativado',   reason: 'Feature concluída em produção' },
];

const PH_TYPE_LABEL = { sync: 'Sincronização', route_add: 'Rota adicionada', route_rm: 'Rota removida', price: 'Preço alterado', limit: 'Limite alterado', create: 'Plano criado', module: 'Módulo alterado' };
const PH_TYPE_COLOR = { sync: 'var(--info)', route_add: 'var(--success)', route_rm: 'var(--danger)', price: 'var(--warning)', limit: 'var(--warning)', create: 'var(--info)', module: 'var(--text)' };

const PlansHistoryModal = ({ open, onClose, extraHistory }) => {
  const [filterPlan, setFilterPlan] = useSM('all');
  const [filterType, setFilterType] = useSM('all');

  if (!open) return null;

  const allHistory = [...(extraHistory || []), ...PLANS_HIST_MOCK];
  const filtered = allHistory.filter(h =>
    (filterPlan === 'all' || h.plan.toLowerCase() === filterPlan) &&
    (filterType === 'all' || h.type === filterType)
  );

  return (
    <ModalOverlay onClose={onClose} wide>
      <MHead title="Histórico de alterações de planos" sub="Registro completo de mudanças aplicadas a planos e permissões" onClose={onClose} />
      <MBody>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <SMSel value={filterPlan} onChange={e => setFilterPlan(e.target.value)} style={{ flex: 1 }}>
            <option value="all">Todos os planos</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </SMSel>
          <SMSel value={filterType} onChange={e => setFilterType(e.target.value)} style={{ flex: 1 }}>
            <option value="all">Todos os tipos</option>
            {Object.entries(PH_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </SMSel>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{filtered.length} reg.</span>
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {filtered.map((h, i) => (
            <div key={h.id} style={{ padding: '12px 16px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: PH_TYPE_COLOR[h.type] || 'var(--text-muted)', flexShrink: 0, marginTop: 5 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{h.resource}</span>
                  <span style={{ fontSize: 11, background: 'var(--bg-subtle)', padding: '1px 8px', borderRadius: 10, color: 'var(--text-muted)' }}>{h.plan}</span>
                  <span style={{ fontSize: 11, color: PH_TYPE_COLOR[h.type], fontWeight: 600 }}>{PH_TYPE_LABEL[h.type]}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: '1px 8px', borderRadius: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{h.before}</span>
                  <Icon name="arrowRight" size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: '1px 8px', borderRadius: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{h.after}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{h.date} · {h.user}{h.reason && <span style={{ fontStyle: 'italic' }}> · {h.reason}</span>}</div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhum registro com os filtros selecionados.</div>
          )}
        </div>
      </MBody>
      <MFoot>
        <button className="btn ghost" onClick={onClose}>Fechar</button>
        <button className="btn" onClick={onClose}><Icon name="download" size={13} />Exportar histórico</button>
      </MFoot>
    </ModalOverlay>
  );
};

// ── 5. SyncPermissionsModal ───────────────────────────────────────────────────
const SyncPermissionsModal = ({ open, onClose, onSync }) => {
  const [scope,         setScope]         = useSM('all');
  const [registerAudit, setRegisterAudit] = useSM(true);
  const [phase,         setPhase]         = useSM('confirm');
  const [progress,      setProgress]      = useSM(0);
  const [result,        setResult]        = useSM(null);

  useEffectSM(() => {
    if (!open) { setPhase('confirm'); setProgress(0); setResult(null); }
  }, [open]);

  if (!open) return null;

  const SCOPES = [
    { v: 'all',      l: 'Sincronizar todos os planos',    sub: '3 planos · 119 tenants impactados' },
    { v: 'selected', l: 'Apenas plano Pro',                sub: '37 tenants impactados' },
    { v: 'outdated', l: 'Apenas tenants desatualizados',   sub: '12 tenants com divergência detectada' },
  ];

  const handleSync = async () => {
    setPhase('progress');
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(r => setTimeout(r, 130));
      setProgress(i);
    }
    const res = { plans: scope === 'all' ? 3 : 1, tenants: scope === 'all' ? 119 : scope === 'selected' ? 37 : 12, permissions: scope === 'all' ? 151 : 46, errors: 0 };
    setResult(res);
    setPhase('result');
    onSync(res);
  };

  return (
    <ModalOverlay onClose={phase === 'progress' ? undefined : onClose}>
      <MHead title="Sincronizar permissões dos planos" sub="Recalcula rotas liberadas e permissões para tenants" onClose={phase === 'progress' ? undefined : onClose} />
      <MBody>
        {phase === 'confirm' && (
          <>
            <div style={{ padding: '12px 16px', background: 'var(--warning-bg)', borderRadius: 10, display: 'flex', gap: 12 }}>
              <Icon name="alert" size={18} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Esta ação recalcula rotas liberadas e permissões por plano. <strong>Tenants podem receber atualização de permissões</strong> conforme o plano atual.
              </div>
            </div>
            <div>
              <SMLbl>Escopo da sincronização</SMLbl>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SCOPES.map(s => (
                  <label key={s.v} style={{ display: 'flex', gap: 12, padding: '10px 14px', border: `2px solid ${scope === s.v ? 'var(--brand-navy)' : 'var(--border)'}`, borderRadius: 10, cursor: 'pointer', background: scope === s.v ? 'color-mix(in srgb,var(--brand-navy) 5%,var(--bg-elevated))' : 'var(--bg-elevated)', transition: 'all 120ms' }}>
                    <input type="radio" name="syncscope" checked={scope === s.v} onChange={() => setScope(s.v)} style={{ accentColor: 'var(--brand-navy)', marginTop: 3 }} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{s.l}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.sub}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <SMChk checked={registerAudit} onChange={e => setRegisterAudit(e.target.checked)} label="Registrar alteração em auditoria" sub="Recomendado: mantém rastreabilidade completa" />
          </>
        )}
        {phase === 'progress' && (
          <div style={{ padding: '32px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <Spinner />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>Sincronizando permissões…</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Recalculando rotas — não feche esta janela</div>
            </div>
            <ProgressBar value={progress} />
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{progress}%</div>
          </div>
        )}
        {phase === 'result' && result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ padding: '14px 16px', background: 'var(--success-bg)', borderRadius: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
              <Icon name="check" size={20} style={{ color: 'var(--success)' }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--success)' }}>Sincronização concluída com sucesso</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Todas as permissões foram recalculadas</div>
              </div>
            </div>
            {[['Planos sincronizados', result.plans, 'layers'], ['Tenants impactados', result.tenants, 'store'], ['Permissões atualizadas', result.permissions, 'shield'], ['Erros', result.errors, 'alert']].map(([l, v, icon]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-subtle)', borderRadius: 8 }}>
                <div style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--text-secondary)', alignItems: 'center' }}><Icon name={icon} size={14} />{l}</div>
                <strong style={{ fontSize: 16, color: l === 'Erros' && v > 0 ? 'var(--danger)' : l === 'Erros' ? 'var(--success)' : 'var(--text)' }}>{v}</strong>
              </div>
            ))}
            <div style={{ padding: '8px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 6 }}>
              <Icon name="clock" size={12} />Última sincronização: agora
            </div>
          </div>
        )}
      </MBody>
      <MFoot>
        {phase === 'confirm' && <>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleSync}><Icon name="refresh" size={13} />Sincronizar</button>
        </>}
        {phase === 'result' && <button className="btn primary" onClick={onClose}><Icon name="check" size={13} />Concluir</button>}
      </MFoot>
    </ModalOverlay>
  );
};

// ── 6. ComparePlansModal ──────────────────────────────────────────────────────
const CMP_DATA = {
  basic:      { name: 'Basic',      price: 'R$ 149/mês',  tenants: 82 },
  pro:        { name: 'Pro',        price: 'R$ 349/mês',  tenants: 37 },
  enterprise: { name: 'Enterprise', price: 'R$ 999/mês',  tenants: 0  },
};
const CMP_ROWS = [
  { section: 'Limites', rows: [
    { label: 'Produtos',         basic: '500',       pro: 'Ilimitado',    enterprise: 'Ilimitado' },
    { label: 'Usuários staff',   basic: '3',         pro: '10',           enterprise: 'Ilimitado' },
    { label: 'Domínios',         basic: '1',         pro: '3',            enterprise: 'Ilimitado' },
    { label: 'Integrações',      basic: '2',         pro: '10',           enterprise: 'Ilimitado' },
    { label: 'Storage',          basic: '5 GB',      pro: '50 GB',        enterprise: '500 GB'    },
    { label: 'Requisições/mês',  basic: '10k',       pro: '100k',         enterprise: 'Ilimitado' },
  ]},
  { section: 'Módulos', rows: [
    { label: 'Pedidos',          basic: '✓', pro: '✓', enterprise: '✓' },
    { label: 'Produtos',         basic: '✓', pro: '✓', enterprise: '✓' },
    { label: 'Tabelas de Preço', basic: '—', pro: '✓', enterprise: '✓' },
    { label: 'App Store',        basic: '—', pro: '✓', enterprise: '✓' },
    { label: 'Blog',             basic: '—', pro: '✓', enterprise: '✓' },
    { label: 'Rel. avançados',   basic: '—', pro: '✓', enterprise: '✓' },
    { label: 'API ilimitada',    basic: '—', pro: '—', enterprise: '✓' },
    { label: 'White-label',      basic: '—', pro: '—', enterprise: '✓' },
    { label: 'Multistore',       basic: '—', pro: '—', enterprise: '✓' },
  ]},
  { section: 'Suporte', rows: [
    { label: 'Canal',            basic: 'E-mail',  pro: 'Chat + E-mail', enterprise: 'Dedicado' },
    { label: 'SLA 1ª resposta',  basic: '48h',     pro: '8h',            enterprise: '1h'       },
  ]},
];

const ComparePlansModal = ({ open, onClose }) => {
  const [selected, setSelected] = useSM(['basic', 'pro', 'enterprise']);
  if (!open) return null;
  const toggle = p => setSelected(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);
  const plans = selected.filter(p => CMP_DATA[p]);
  const cellVal = (row, plan) => row[plan] || '—';
  const isDiff = row => new Set(plans.map(p => cellVal(row, p))).size > 1;

  return (
    <ModalOverlay onClose={onClose} wide>
      <MHead title="Comparar planos" sub="Visão lado a lado de limites, módulos e recursos" onClose={onClose} />
      <MBody>
        <div style={{ display: 'flex', gap: 8 }}>
          {Object.entries(CMP_DATA).map(([k, p]) => (
            <button key={k} onClick={() => toggle(k)} style={{ flex: 1, padding: '8px 12px', border: `2px solid ${selected.includes(k) ? 'var(--brand-navy)' : 'var(--border)'}`, borderRadius: 10, background: selected.includes(k) ? 'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))' : 'var(--bg-elevated)', color: selected.includes(k) ? 'var(--brand-navy)' : 'var(--text-muted)', cursor: 'pointer', fontWeight: selected.includes(k) ? 700 : 400, transition: 'all 120ms' }}>
              <div style={{ fontSize: 14 }}>{p.name}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{p.price}</div>
            </button>
          ))}
        </div>
        {plans.length < 2 && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 24 }}>Selecione pelo menos 2 planos para comparar.</div>
        )}
        {plans.length >= 2 && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `180px ${plans.map(() => '1fr').join(' ')}`, background: 'var(--bg-subtle)', padding: '8px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recurso</div>
              {plans.map(p => <div key={p} style={{ fontSize: 13, fontWeight: 700, textAlign: 'center' }}>{CMP_DATA[p].name}</div>)}
            </div>
            {CMP_ROWS.map(sec => (
              <React.Fragment key={sec.section}>
                <div style={{ padding: '6px 14px', background: 'color-mix(in srgb,var(--brand-navy) 4%,var(--bg-subtle))', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--brand-navy)', borderTop: '1px solid var(--border)' }}>
                  {sec.section}
                </div>
                {sec.rows.map(row => (
                  <div key={row.label} style={{ display: 'grid', gridTemplateColumns: `180px ${plans.map(() => '1fr').join(' ')}`, padding: '9px 14px', borderTop: '1px solid var(--border)', background: isDiff(row) ? 'color-mix(in srgb,var(--warning-bg) 40%,transparent)' : 'transparent', alignItems: 'center' }}>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 6, alignItems: 'center' }}>
                      {isDiff(row) && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warning)', flexShrink: 0 }} />}
                      {row.label}
                    </div>
                    {plans.map(p => {
                      const v = cellVal(row, p);
                      return (
                        <div key={p} style={{ textAlign: 'center', fontSize: 13, fontWeight: v === '✓' ? 600 : 400, color: v === '✓' ? 'var(--success)' : v === '—' ? 'var(--text-muted)' : 'var(--text)' }}>
                          {v}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
        {plans.length >= 2 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warning)', display: 'inline-block' }} />
            Linha destacada = diferença entre planos selecionados
          </div>
        )}
      </MBody>
      <MFoot>
        <button className="btn ghost" onClick={onClose}>Fechar</button>
        <button className="btn" onClick={onClose}><Icon name="download" size={13} />Exportar comparação</button>
      </MFoot>
    </ModalOverlay>
  );
};

// ── 7. NewPlanModal ───────────────────────────────────────────────────────────
const NewPlanModal = ({ open, onClose, onCreate }) => {
  const emptyForm = { name: '', code: '', description: '', monthlyPrice: '', yearlyPrice: '', limitProducts: '500', limitUsers: '3', limitIntegrations: '5', limitOrders: '10000', modules: { catalog: true, orders: true, billing: false, integrations: false, reports: false, support: false }, status: 'draft', notes: '' };
  const [form, setForm] = useSM(emptyForm);
  const [saving, setSaving] = useSM(false);

  useEffectSM(() => { if (!open) { setForm(emptyForm); setSaving(false); } }, [open]);
  if (!open) return null;

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const toggleMod = k => setForm(f => ({ ...f, modules: { ...f.modules, [k]: !f.modules[k] } }));
  const canCreate = form.name.trim() && form.code.trim();
  const inpSt = { width: '100%', height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };

  const handleCreate = async (asDraft) => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 700));
    setSaving(false);
    const statusMap = { draft: 'rascunho', active: 'ativo', hidden: 'oculto' };
    onCreate({ ...form, id: form.code || ('plan_' + Date.now()), status: asDraft ? 'rascunho' : statusMap[form.status] || 'rascunho', tenantsCount: 0, routesCount: 45, monthlyPrice: +form.monthlyPrice || 0, yearlyPrice: +form.yearlyPrice || 0, monthlyRevenue: 0, features: Object.entries(form.modules).filter(([, v]) => v).map(([k]) => k), updatedAt: 'Agora', createdAt: 'Agora' });
  };

  return (
    <ModalOverlay onClose={onClose} wide>
      <MHead title="Novo plano da plataforma" sub="Criar plano disponível para contratação pelos tenants" onClose={onClose} />
      <MBody>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><SMLbl>Nome do plano *</SMLbl><input style={inpSt} value={form.name} onChange={set('name')} placeholder="Ex: Starter" /></div>
          <div><SMLbl>Código técnico *</SMLbl><input style={{ ...inpSt, fontFamily: 'JetBrains Mono, monospace' }} value={form.code} onChange={set('code')} placeholder="starter" /></div>
          <div><SMLbl>Preço mensal (R$)</SMLbl><input style={inpSt} type="number" value={form.monthlyPrice} onChange={set('monthlyPrice')} placeholder="149" /></div>
          <div><SMLbl>Preço anual (R$)</SMLbl><input style={inpSt} type="number" value={form.yearlyPrice} onChange={set('yearlyPrice')} placeholder="1490" /></div>
        </div>
        <div><SMLbl>Descrição</SMLbl><textarea value={form.description} onChange={set('description')} rows={2} placeholder="Descrição do plano para exibição pública..." style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} /></div>
        <div>
          <SMSecTitle>Limites</SMSecTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['limitProducts','Produtos'],['limitUsers','Usuários internos'],['limitIntegrations','Integrações'],['limitOrders','Pedidos/mês']].map(([k,l]) => (
              <div key={k}><SMLbl>{l}</SMLbl><input style={inpSt} value={form[k]} onChange={set(k)} placeholder="ex: 500 ou Ilimitado" /></div>
            ))}
          </div>
        </div>
        <div>
          <SMSecTitle>Módulos incluídos</SMSecTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[['catalog','Catálogo'],['orders','Pedidos'],['billing','Billing'],['integrations','Integrações'],['reports','Relatórios'],['support','Suporte']].map(([k,l]) => (
              <label key={k} style={{ display: 'flex', gap: 8, padding: '8px 12px', border: `1px solid ${form.modules[k] ? 'var(--brand-navy)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: form.modules[k] ? 'color-mix(in srgb,var(--brand-navy) 6%,var(--bg-elevated))' : 'var(--bg-elevated)', alignItems: 'center', transition: 'all 120ms' }}>
                <input type="checkbox" checked={form.modules[k]} onChange={() => toggleMod(k)} style={{ accentColor: 'var(--brand-navy)' }} />
                <span style={{ fontSize: 13, fontWeight: form.modules[k] ? 600 : 400, color: form.modules[k] ? 'var(--brand-navy)' : 'var(--text-muted)' }}>{l}</span>
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <SMLbl>Status inicial</SMLbl>
            <SMSel value={form.status} onChange={set('status')}>
              <option value="draft">Rascunho</option>
              <option value="active">Ativo</option>
              <option value="hidden">Oculto</option>
            </SMSel>
          </div>
          <div><SMLbl>Observação interna</SMLbl><input style={inpSt} value={form.notes} onChange={set('notes')} placeholder="Opcional..." /></div>
        </div>
        <div style={{ padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          A criação do plano será registrada no log de auditoria.
        </div>
      </MBody>
      <MFoot>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn" onClick={() => handleCreate(true)} disabled={!canCreate || saving}><Icon name="edit" size={13} />Criar como rascunho</button>
        <button className="btn primary" onClick={() => handleCreate(false)} disabled={!canCreate || saving}>
          {saving ? <><Icon name="refresh" size={13} style={{ animation: 'spin 1s linear infinite' }} />Criando…</> : <><Icon name="plus" size={13} />Criar plano</>}
        </button>
      </MFoot>
    </ModalOverlay>
  );
};

// ── 8. ExportMdModal ──────────────────────────────────────────────────────────
const ExportMdModal = ({ open, onClose, onExport }) => {
  const [sections, setSections] = useSM(['all']);
  const [format,   setFormat]   = useSM('md');
  const [opts,     setOpts]     = useSM({ endpoints: true, models: true, flows: false, architecture: false, changelog: false });
  const [phase,    setPhase]    = useSM('idle');
  const [progress, setProgress] = useSM(0);

  useEffectSM(() => { if (!open) { setPhase('idle'); setProgress(0); } }, [open]);
  if (!open) return null;

  const toggleSection = s => setSections(prev => prev.includes('all') ? [s] : prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleAll = () => setSections(s => s.includes('all') ? [] : ['all']);
  const setOpt = k => e => setOpts(o => ({ ...o, [k]: e.target.checked }));

  const handleExport = async () => {
    setPhase('progress');
    for (let i = 0; i <= 100; i += 20) {
      await new Promise(r => setTimeout(r, 160));
      setProgress(i);
    }
    setPhase('result');
    onExport({ sections, format, opts });
  };

  const fileNames = { md: 'SUPER_ADMIN_BACKEND_SPEC.md', json: 'super-admin-spec.json', pdf: 'SUPER_ADMIN_SPEC.pdf' };

  return (
    <ModalOverlay onClose={phase === 'progress' ? undefined : onClose}>
      <MHead title="Exportar especificação técnica" sub="Gera documentação do backend para implementação" onClose={phase === 'progress' ? undefined : onClose} />
      <MBody>
        {phase === 'idle' && (
          <>
            <div>
              <SMLbl>Seções a exportar</SMLbl>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', padding: '7px 10px', background: sections.includes('all') ? 'color-mix(in srgb,var(--brand-navy) 6%,var(--bg-elevated))' : 'var(--bg-elevated)', borderRadius: 8, border: `1px solid ${sections.includes('all') ? 'var(--brand-navy)' : 'var(--border)'}`, transition: 'all 120ms' }}>
                  <input type="checkbox" checked={sections.includes('all')} onChange={toggleAll} style={{ accentColor: 'var(--brand-navy)' }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Todas as seções</span>
                </label>
                <div style={{ paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {[['platform','Plataforma'],['support','Suporte & KB'],['permissions','Permissões'],['billing','Billing'],['observability','Observabilidade'],['integrations','Integrações']].map(([k,l]) => (
                    <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', padding: '5px 10px', borderRadius: 6, opacity: sections.includes('all') ? 0.5 : 1 }}>
                      <input type="checkbox" checked={sections.includes('all') || sections.includes(k)} onChange={() => toggleSection(k)} disabled={sections.includes('all')} style={{ accentColor: 'var(--brand-navy)' }} />
                      <span style={{ fontSize: 13 }}>{l}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <SMLbl>Formato de saída</SMLbl>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['md','Markdown (.md)'],['json','JSON'],['pdf','PDF']].map(([v, l]) => (
                  <button key={v} onClick={() => setFormat(v)} style={{ flex: 1, padding: '8px 4px', border: `2px solid ${format === v ? 'var(--brand-navy)' : 'var(--border)'}`, borderRadius: 8, background: format === v ? 'color-mix(in srgb,var(--brand-navy) 8%,var(--bg-elevated))' : 'var(--bg-elevated)', color: format === v ? 'var(--brand-navy)' : 'var(--text-muted)', fontWeight: format === v ? 700 : 400, fontSize: 12, cursor: 'pointer', transition: 'all 120ms' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <SMSecTitle>Incluir no export</SMSecTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SMChk checked={opts.endpoints}    onChange={setOpt('endpoints')}    label="Endpoints REST" />
                <SMChk checked={opts.models}       onChange={setOpt('models')}       label="Modelos Django" />
                <SMChk checked={opts.flows}        onChange={setOpt('flows')}        label="Fluxos de dados" />
                <SMChk checked={opts.architecture} onChange={setOpt('architecture')} label="Notas de arquitetura" />
                <SMChk checked={opts.changelog}    onChange={setOpt('changelog')}    label="Changelog" />
              </div>
            </div>
          </>
        )}
        {phase === 'progress' && (
          <div style={{ padding: '32px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <Spinner />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Gerando arquivo…</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Compilando seções e formatando documentação</div>
            </div>
            <ProgressBar value={progress} />
          </div>
        )}
        {phase === 'result' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ padding: '14px 16px', background: 'var(--success-bg)', borderRadius: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
              <Icon name="check" size={20} style={{ color: 'var(--success)' }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--success)' }}>Arquivo gerado com sucesso</div>
                <code style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)' }}>{fileNames[format]}</code>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn primary" style={{ flex: 1 }} onClick={onClose}><Icon name="download" size={13} />Baixar arquivo</button>
              <button className="btn" style={{ flex: 1 }} onClick={onClose}><Icon name="copy" size={13} />Copiar conteúdo</button>
            </div>
            <div style={{ padding: '8px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'center' }}>
              <Icon name="clock" size={12} />Última exportação: agora
            </div>
          </div>
        )}
      </MBody>
      {phase === 'idle' && (
        <MFoot>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleExport}><Icon name="download" size={13} />Exportar</button>
        </MFoot>
      )}
    </ModalOverlay>
  );
};

export {
  ComparePlansModal,
  ExportHistoryModal,
  ExportMdModal,
  NewPlanModal,
  PlansHistoryModal,
  RetentionConfigModal,
  SecuritySettingsModal,
  SyncPermissionsModal,
};
